/**
 * The toast host.
 *
 * Three things that hand-rolled toast stacks usually get wrong:
 *
 * - **Errors do not auto-dismiss.** A message that disappears after four
 *   seconds is useless to anyone who was not looking at that corner of the
 *   screen, and a failure is exactly the message you cannot afford to miss.
 * - **The live region exists before the toast does.** A region inserted into
 *   the DOM at the same moment as its content is frequently not announced at
 *   all; the container must already be there for the insertion to register.
 * - **Dismissal is reachable.** Each toast has a real close button, so a
 *   keyboard user is not stuck waiting out a timer.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { DEFAULT_TOAST_MS, PERSISTENT, ToastContext, type Toast, type ToastOptions } from "./toast";
import "./feedback.css";

export interface ToasterProps {
  children: ReactNode;
  /** Announced label of the region, translated by the caller. */
  regionLabel?: string;
  /** Accessible name of each dismiss button, translated by the caller. */
  dismissLabel?: string;
}

export function Toaster({
  children,
  regionLabel = "Notifications",
  dismissLabel = "Dismiss",
}: ToasterProps) {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const counter = useRef(0);
  // Cleared on unmount so a pending timer cannot set state on a gone tree.
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback(
    (message: string, options: ToastOptions = {}) => {
      const tone = options.tone ?? "info";
      // An error that vanishes is an error nobody read. Default failures to
      // persistent and let the caller override deliberately.
      const durationMs = options.durationMs ?? (tone === "danger" ? PERSISTENT : DEFAULT_TOAST_MS);

      counter.current += 1;
      const id = `toast-${String(counter.current)}`;
      setToasts((current) => [...current, { id, tone, message, durationMs }]);

      if (durationMs > 0) {
        timers.current.set(
          id,
          setTimeout(() => {
            dismiss(id);
          }, durationMs),
        );
      }
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const value = useMemo(() => ({ toasts, show, dismiss }), [toasts, show, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {/*
        Rendered unconditionally, even with nothing in it. A live region added
        to the DOM at the same instant as its first message is often not
        announced — the container has to be present for the insertion to
        register as a change.
      */}
      <div
        className="hardauto-toaster"
        role="region"
        aria-label={regionLabel}
        aria-live="polite"
        // Assistive technology reads the whole region, so a second toast does
        // not re-announce the first.
        aria-atomic="false"
      >
        {toasts.map((toast) => (
          <div key={toast.id} className="hardauto-toast" data-tone={toast.tone}>
            <p className="hardauto-toast__message">{toast.message}</p>
            <button
              type="button"
              className="hardauto-toast__dismiss"
              onClick={() => {
                dismiss(toast.id);
              }}
              aria-label={`${dismissLabel}: ${toast.message}`}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
